import React, { useContext, useEffect, useState } from 'react';
import {Mui, MuiIcons, MuiLab} from "@leap4/frontend";
import {IClient} from "./IClient";
import {getClientForOrganization, postNewClient, regenerateClient} from "../../../../actions/users";
import {AppDispatch} from "../../../../store/store";
import {OrganizationContext} from "../OrganizationContex";

const {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, Typography, Tooltip} = Mui;
const {ContentCopy: ContentCopyIcon} = MuiIcons;
const {LoadingButton} = MuiLab;

interface ClientFormProps {
    organizationId: string;
    dispatch: AppDispatch
}

export const ClientForm: React.FC<ClientFormProps> = ({ organizationId, dispatch }) => {
    const [clientKey, setClientKey] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [isGenerated, setIsGenerated] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const MASKED_SECRET = '*'.repeat(50);
    const { setClientCredentials } = useContext(OrganizationContext);

    useEffect(() => {
        if (!clientKey || !secretKey) {
            setClientCredentials(null);
            return;
        }
        const isVisible = secretKey !== MASKED_SECRET;
        setClientCredentials({
            clientKey,
            visibleSecret: isVisible ? secretKey : null,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientKey, secretKey]);

    useEffect(() => {
        const fetchClient = async () => {
            try {
                const existingClient = await getClientForOrganization(dispatch, organizationId) as IClient;
                if (existingClient?.clientKey) {
                    setClientKey(existingClient.clientKey);
                    setSecretKey(MASKED_SECRET); // mask the secret visually
                    setIsGenerated(true);
                }
            } catch (err) {
                console.error('Failed to fetch client:', err);
            }
        };

        fetchClient();
    }, [organizationId, dispatch]);

    const handleGenerateClient = async () => {
        setError('');
        const res = await postNewClient(dispatch, {
            clientKey,
            allowedDemandPartnerIds: [],
            organizationId,
        });

        if (res?.secretKey) {
            setSecretKey(res.secretKey);
            setIsGenerated(true);
        } else {
            setError('Client already exists or failed to generate');
        }
    };

    const handleRegenerateClient = async () => {
        setIsRegenerating(true);
        const res = await regenerateClient(dispatch, organizationId);
        if (res?.secretKey) {
            setClientKey(res.clientKey);
            setSecretKey(res.secretKey);
        }
        setIsRegenerating(false);
        setConfirmOpen(false);
    };


    const handleCopy = () => {
        navigator.clipboard.writeText(secretKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <>
            <Typography variant="h5" mb={2}>Client Setup</Typography>

            <TextField
                fullWidth
                label="Client Key"
                value={clientKey}
                onChange={(e) => setClientKey(e.target.value)}
                disabled={isGenerated}
                margin="normal"
            />

            <Box display="flex" alignItems="center" gap={1}>
                <TextField
                    fullWidth
                    label="Secret Key"
                    value={secretKey}
                    disabled
                    margin="normal"
                    helperText={
                        secretKey !== MASKED_SECRET && !!secretKey
                            ? 'This will be shown only once. Save it securely.'
                            : ''
                    }
                />
                {secretKey !== MASKED_SECRET && !!secretKey && (
                    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton onClick={handleCopy}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                )}

            </Box>

            {error && (
                <Typography color="error" variant="body2" mt={1}>
                    {error}
                </Typography>
            )}

            {!isGenerated && (
                <Box mt={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateClient}
                        disabled={!clientKey}
                    >
                        Generate Client
                    </Button>
                </Box>
            )}

            {isGenerated && (
                <Box mt={2}>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setConfirmOpen(true)}
                        disabled={isRegenerating}
                    >
                        Regenerate Client
                    </Button>
                </Box>
            )}

            <Dialog open={confirmOpen} onClose={() => !isRegenerating && setConfirmOpen(false)}>
                <DialogTitle>Regenerate client secret?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This immediately invalidates the existing secret. Any partner integration
                        using the old secret will stop working until it is updated. The new secret
                        will be shown only once.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => setConfirmOpen(false)}
                        loading={isRegenerating}
                    >
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        onClick={handleRegenerateClient}
                        color="warning"
                        variant="contained"
                        loading={isRegenerating}
                    >
                        Regenerate
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );

};
